import { useState } from 'react';
import type { ControlCategory, DirectionItem, DirectionItemKind, DirectionLens } from '@life-os/app-core';
import { CONTROL_CATEGORIES, LENS_KINDS } from '@life-os/app-core';
import { Button, Chip, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const POSTURE_TONE: Record<ControlCategory, ChipTone> = {
  control: 'primary',
  influence: 'warn',
  accept: 'muted',
};

const POSTURE_LABEL: Record<ControlCategory, string> = {
  control: 'in my control',
  influence: 'I can influence',
  accept: 'to accept',
};

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

export interface DirectionLensViewProps {
  readonly lens: DirectionLens;
  readonly title: string;
  readonly subtitle: string;
  readonly placeholder: string;
}

/** Shared screen for the three Direction sub-lenses (roles, values, challenges). */
export function DirectionLensView({ lens, title, subtitle, placeholder }: DirectionLensViewProps) {
  const services = useServices();
  const { revision, refresh } = useStore();
  const kinds = LENS_KINDS[lens];
  const showPosture = lens === 'challenges';

  const [itemKind, setItemKind] = useState<DirectionItemKind>(kinds[0]!);
  const [titleText, setTitleText] = useState('');
  const [detail, setDetail] = useState('');

  const items = useAsync<readonly DirectionItem[]>(() => services.direction.listItems(lens), [revision, lens]);
  const list = items.value ?? [];

  const add = async () => {
    if (titleText.trim().length === 0) return;
    await services.direction.addItem({
      lens,
      itemKind,
      title: titleText,
      detail,
      posture: showPosture ? 'control' : null,
    });
    setTitleText('');
    setDetail('');
    refresh();
  };

  return (
    <>
      <ScreenHeader title={<>{title}</>} subtitle={<>{subtitle}</>} />

      <Pane title="Add">
        <Stack>
          <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
            {kinds.length > 1 ? (
              <select value={itemKind} onChange={(e) => setItemKind(e.target.value as DirectionItemKind)} aria-label="Kind" style={inputStyle}>
                {kinds.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            ) : null}
            <input
              className="lc-cmdbar"
              style={{ flex: '1 1 220px' }}
              value={titleText}
              placeholder={placeholder}
              onChange={(e) => setTitleText(e.target.value)}
              aria-label="Title"
            />
            <Button variant="primary" onClick={() => void add()}>
              Add
            </Button>
          </div>
          <input
            className="lc-cmdbar"
            value={detail}
            placeholder="A line of detail (optional)…"
            onChange={(e) => setDetail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add();
            }}
            aria-label="Detail"
          />
        </Stack>
      </Pane>

      <Pane title={title} hint={`${list.length}`}>
        <Stack>
          {list.length === 0 ? (
            <EmptyState title="Nothing here yet">{placeholder}</EmptyState>
          ) : (
            list.map((item) => (
              <div key={String(item.id)} className="lc-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap' }}>
                  <strong style={{ flex: 1 }}>{item.title}</strong>
                  {kinds.length > 1 ? <Chip tone="muted">{item.itemKind}</Chip> : null}
                  {showPosture && item.posture ? <Chip tone={POSTURE_TONE[item.posture]}>{POSTURE_LABEL[item.posture]}</Chip> : null}
                  {showPosture ? (
                    <select
                      value={item.posture ?? 'control'}
                      onChange={(e) => void services.direction.setItemPosture(item.id, e.target.value as ControlCategory).then(refresh)}
                      aria-label="Posture"
                      style={inputStyle}
                    >
                      {CONTROL_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {POSTURE_LABEL[c]}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  <Button variant="ghost" onClick={() => void services.direction.archiveItem(item.id).then(refresh)}>
                    Archive
                  </Button>
                </div>
                {item.detail ? (
                  <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>{item.detail}</div>
                ) : null}
              </div>
            ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
